import { create } from 'zustand'
import { apiFetch } from '@/lib/api'
import { teardownSession } from '@/lib/session'
import type { User } from '@/types/api'

type AuthState = {
  user: User | null
  loading: boolean
  refresh: () => Promise<void>
  sendPhoneCode: (phone: string) => Promise<PhoneCodeResult>
  loginWithPhone: (phone: string, code: string) => Promise<void>
  registerWithPhone: (phone: string, code: string, name?: string) => Promise<void>
  logout: () => Promise<void>
}

/** /auth/phone/send-code payload. `devCode` exists only in VITE_MOCK mode
 * (the mock has no real SMS channel; the real backend never returns it). */
type PhoneCodeResult = { phone: string; expiresAt: string; devCode?: string }

/** Every session endpoint's data payload is { user } — dig it in one place. */
async function fetchUser(path: string, init?: RequestInit): Promise<User | null> {
  return (await apiFetch<{ user: User | null }>(path, init)).user ?? null
}

export const useAuth = create<AuthState>((set, get) => ({
  user: null,
  loading: true,

  refresh: async () => {
    // 已有 user 时静默再验证：AuthGate 每次路由切换都调 refresh，
    // 置 loading 会让 Protected 全屏 spinner 并卸载整棵 Layout 子树。
    set({ loading: get().user == null })
    try {
      const user = await fetchUser('/auth/me')
      if (user == null) teardownSession()
      set({ user, loading: false })
    } catch (e) {
      // 401 = definitive session loss → wipe tenant state; transient errors
      // (network/5xx) keep stored keys so a hiccup doesn't force re-paste.
      // No session = unauthenticated, not an error state.
      if (e instanceof Error && e.message.includes('401')) teardownSession()
      set({ user: null, loading: false })
    }
  },

  sendPhoneCode: async (phone) => {
    // /auth/phone/send-code → { phone, expiresAt } (devCode in mock mode)
    return apiFetch<PhoneCodeResult>('/auth/phone/send-code', {
      method: 'POST',
      body: JSON.stringify({ phone }),
    })
  },

  loginWithPhone: async (phone, code) => {
    // /auth/phone/login answers { user } and sets the cookie;
    // unregistered numbers throw ApiError('auth.USER_NOT_FOUND', 404) —
    // the page switches to the register surface.
    const user = await fetchUser('/auth/phone/login', {
      method: 'POST',
      body: JSON.stringify({ phone, code }),
    })
    teardownSession()
    if (user) {
      set({ user })
      return
    }
    // Fallback: hydrate via /auth/me
    set({ user: await fetchUser('/auth/me') })
  },

  registerWithPhone: async (phone, code, name) => {
    // /auth/phone/register → 201 { user } + cookie (phoneVerified: true)
    const user = await fetchUser('/auth/phone/register', {
      method: 'POST',
      body: JSON.stringify({ phone, code, ...(name ? { name } : {}) }),
    })
    teardownSession()
    if (user) {
      set({ user })
      return
    }
    set({ user: await fetchUser('/auth/me') })
  },

  logout: async () => {
    await apiFetch('/auth/logout', { method: 'POST' })
    teardownSession()
    set({ user: null })
  },
}))
